import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import AuthBackground from '../components/AuthBackground';
import Tabs from '../components/Tabs';
import TextField from '../components/TextField';
import Button from '../components/Button';
// import SocialRow from '../components/SocialRow';
import Toast from '../components/Toast';
import PasswordRules from '../components/PasswordRules';

const Signup = () => {
  const navigate = useNavigate();
  const [activeTab] = useState('Sign Up');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
    city: '',
    country: '',
    acceptTerms: false,
    acceptMarketing: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!formData.fullName) newErrors.fullName = 'Name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.gender) newErrors.gender = 'Please choose a gender';
    
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    
    const validatePassword = (password) => {
      return password.length >= 8 && /\d/.test(password) && /[a-zA-Z]/.test(password);
    };

    if (!validatePassword(formData.password)) {
      newErrors.password = 'Password does not meet the requirements';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.acceptTerms) {
      newErrors.terms = 'Please accept the terms and conditions';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      try {
        const response = await api.post('/accounts/signup/', {
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          confirm_password: formData.confirmPassword,
          gender: formData.gender,
          city: formData.city,
          country: formData.country,
          accept_terms: formData.acceptTerms,
          accept_marketing: formData.acceptMarketing,
        });

        if (response.status === 201 || response.status === 200) {
          setToast({ type: 'success', message: 'Account created successfully!' });
          setTimeout(() => navigate('/auth'), 1500);
        }
      } catch (error) {
        if (error.response && error.response.data) {
          const backendErrors = {};
          for (const key in error.response.data) {
            backendErrors[key] = error.response.data[key][0];
          }
          setErrors(backendErrors);
          setToast({ type: 'error', message: 'Please correct the errors below.' });
        } else {
          setToast({ type: 'error', message: 'An unexpected error occurred. Please try again.' });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (tab) => {
    if (tab === 'Log In') {
      navigate('/auth');
    }
  };

  return (
    <>
      <AuthBackground />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8 lg:py-12">
        <div className="w-full max-w-md lg:max-w-2xl">
          <div className="bg-white lg:rounded-3xl lg:shadow-xl p-6 lg:p-10 signup-container">
            <div className="text-center mb-6">
              <h1 className="text-3xl lg:text-4xl font-bold text-[#0C1D40]">Let's Get Started</h1>
              <p className="text-gray-600 mt-2">Create your account to track your child's journey</p>
            </div>

            <Tabs
              tabs={['Log In', 'Sign Up']}
              activeTab={activeTab}
              onChange={handleTabChange}
              className="mb-6"
            />

            <div className="space-y-4">
              <TextField
                label="Full Name"
                placeholder="Enter your name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                error={errors.fullName}
              />
              <TextField
                label="Email"
                placeholder="Enter your email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={errors.email}
              />
              <TextField
                label="Phone Number"
                placeholder="Enter your phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                error={errors.phone}
              />
              <div className="space-y-1">
                <TextField
                  label="Password"
                  placeholder="Create a password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  error={errors.password}
                />
                <PasswordRules password={formData.password} />
              </div>
              <TextField
                label="Confirm Password"
                placeholder="Confirm your password"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                error={errors.confirmPassword}
              />

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#0C1D40]">Choose Gender</label>

                <div role="radiogroup" className="grid grid-cols-2 gap-4">
                  {['female', 'male'].map((opt) => {
                    const active = formData.gender === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setFormData({ ...formData, gender: opt })}
                        className={
                          `w-full rounded-2xl px-6 py-4 text-center border transition 
             focus:outline-none focus:ring-2 focus:ring-pink-300
             ${active
                            ? 'bg-rose-50 border-rose-200 text-[#0C1D40] shadow-sm'
                            : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'}`
                        }
                      >
                        {opt === 'female' ? 'Female' : 'Male'}
                      </button>
                    );
                  })}
                </div>

                {/* optional error text */}
                {errors.gender && <p className="text-sm text-red-500">{errors.gender}</p>}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TextField
                  label="City"
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
                <TextField
                  label="Country"
                  placeholder="Enter your country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                    className="mr-2 mt-1"
                  />
                  <span className="text-sm">
                    I agree to the <a href="/terms" className="text-pink-500 hover:underline">Terms and Conditions</a> and <a href="/privacy" className="text-pink-500 hover:underline">Privacy Policy</a>
                  </span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.acceptMarketing}
                    onChange={(e) => setFormData({ ...formData, acceptMarketing: e.target.checked })}
                    className="mr-2 mt-1"
                  />
                  <span className="text-sm">Yes, I'd like to receive updates, newsletters, and exclusive offers from TIFL.</span>
                </label>
              </div>
              {errors.terms && (
                <p className="text-sm text-red-500">{errors.terms}</p>
              )}

              <Button
                fullWidth
                onClick={handleSubmit}
                loading={loading}
              >
                Sign Up
              </Button>

              {/* <div className="flex items-center gap-4 my-6">
                <hr className="flex-1 border-gray-300" />
                <span className="text-gray-500 text-sm">Or continue with</span>
                <hr className="flex-1 border-gray-300" />
              </div>

              <SocialRow /> */}

              <div className="text-center mt-6">
                <span className="text-gray-600">Already have an account? </span>
                <button onClick={() => navigate('/auth')} className="text-pink-500 hover:underline font-semibold">
                  Log In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
    </>
  );
};

export default Signup;
